import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthContext";
import { accountService } from "../../Api/Services/accountService";
import { EditUserDataStep } from "../../Enums/EditUserDataStep";
import { EditUserDataType } from "../../Enums/EditUserDataType";
import { toast } from "react-toastify";
import { toPublicPath } from "../../Utils/publicRoutes";
import EditUserDataModal from "../EditUserDataModal/EditUserDataModal";
import EditUserDataInfoModal from "../EditUserDataInfoModal/EditUserDataInfoModal";
import VerificationCodeModal from "../VerificationCodeModal/VerificationCodeModal";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

type EditUserDataFlowProps = {
  isOpen: boolean;
  onClose: () => void;
  method: EditUserDataType;
}

const EditUserDataFlow = ({ isOpen, onClose, method }: EditUserDataFlowProps) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [step, setStep] = useState<EditUserDataStep | null>(null);
  const [pendingData, setPendingData] = useState<any>(null);

  const handleCloseAll = useCallback(() => {
    setStep(null);
    setPendingData(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {    
      setStep(EditUserDataStep.EnterNewData);
    } else {
      handleCloseAll();
    }
  }, [isOpen, handleCloseAll]);

  const handleEnterNewDataSuccess = async (data: any) => {
    setPendingData(data);
    try {
      // The API answers an empty id when the WhatsApp message could not be sent
      const sent = await accountService.sendVerificationCode();
      if (sent && sent !== EMPTY_GUID) {
        setStep(EditUserDataStep.EnterVerificationCode);
      } else {
        toast.error("Não foi possível enviar o código pelo WhatsApp. Verifique se sua conta tem um celular cadastrado ou fale com a loja.");
      }
    } catch {
      toast.error("Erro ao enviar código de verificação, tente novamente mais tarde.");
    }
  };

  const handleEnterVerificationCodeSuccess = async (code: string) => {
    try {
      let updated = false;
      switch (method) {
        case EditUserDataType.Email:
          try {
            const emailUpdated = await accountService.updateEmail({
              newEmail: pendingData.newEmail,
              verificationCode: code
            });
            if (!emailUpdated) {
              toast.error("Erro ao atualizar e-mail.");
              return;
            }
            updated = true;
          } catch (err: any) {
            const message = err?.response?.data as string | undefined;
            toast.error(message ?? "Erro ao atualizar e-mail.");
            return;
          }
          break;

        case EditUserDataType.Password:
          updated = await accountService.updatePassword({
            currentPassword: pendingData.currentPassword,
            newPassword: pendingData.newPassword,
            verificationCode: code
          });
          break;
      }

      if (updated) {
        const successMessage = method === EditUserDataType.Email
          ? "E-mail alterado com sucesso. Faça login novamente."
          : "Senha alterada com sucesso. Faça login novamente.";

        toast.success(successMessage);
        handleCloseAll();
        logout();
        navigate(toPublicPath("/login"));
      } else {
        toast.error("Código inválido, tente novamente.");
      }
    } catch (error) {
      toast.error("Erro ao atualizar dados, tente novamente mais tarde.");
      handleCloseAll();
    }
  };

  return (
    <div>
      <EditUserDataModal
        isOpen={step === EditUserDataStep.EnterNewData}
        onClose={handleCloseAll}
        onSuccess={handleEnterNewDataSuccess}
        method={method}
      />

      <VerificationCodeModal
        isOpen={step === EditUserDataStep.EnterVerificationCode}
        onClose={handleCloseAll}
        onSuccess={handleEnterVerificationCodeSuccess}
        method={method}
      />

      <EditUserDataInfoModal
        isOpen={step === EditUserDataStep.ConfirmDataChange}
        onClose={handleCloseAll}
        method={method}
      />
    </div>
  );
};

export default EditUserDataFlow;
